import { useMemo } from 'react';
import { useCurrentFrame, useVideoConfig } from 'remotion';
import { VideoItem } from './video-item-type';
import { calculateReframeCrop, type ReframeCropResult } from './reframe-utils';
import {reframeSourceTimeMs} from '@waka/compat/alpha-reframe';

/**
 * 매 프레임마다 리프레임 crop 결과를 계산하는 훅.
 * reframeMeta가 없거나 유효하지 않으면 null을 반환한다 (기존 렌더링 fallback).
 */
export const useReframeCrop = (
  item: VideoItem,
  compositionWidth: number,
  compositionHeight: number,
  assetStartDeltaInSeconds = 0,
  assetDimensions?: {width: number; height: number},
): ReframeCropResult | null => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  return useMemo(() => {
    if (!item.reframeMeta) return null;

    // 원본 해상도: reframeMeta.videoWidth/Height > sourceVideoWidth/Height 순으로 사용
    const videoWidth = item.reframeMeta.videoWidth ?? item.sourceVideoWidth ?? assetDimensions?.width;
    const videoHeight = item.reframeMeta.videoHeight ?? item.sourceVideoHeight ?? assetDimensions?.height;

    if (!videoWidth || !videoHeight) return null;

    // 원본 영상 기준 절대 시간(ms) 계산
    const currentTimeMs = reframeSourceTimeMs({
      videoStartFromInSeconds: item.videoStartFromInSeconds,
      assetStartDeltaInSeconds,
      frame,
      fps,
      playbackRate: item.playbackRate,
    });

    return calculateReframeCrop({
      reframeMeta: item.reframeMeta,
      currentTimeMs,
      videoWidth,
      videoHeight,
      compositionWidth,
      compositionHeight,
    });
  }, [
    item.reframeMeta,
    item.sourceVideoWidth,
    item.sourceVideoHeight,
    assetDimensions?.width,
    assetDimensions?.height,
    item.videoStartFromInSeconds,
    item.playbackRate,
    assetStartDeltaInSeconds,
    frame,
    fps,
    compositionWidth,
    compositionHeight,
  ]);
};
